<template>
  <div class="h-full">
    <BaseTable
      table-id="customer-details-trader-sites"
      :headers="tableHeaders"
      :list="filteredSites"
      hide-grouping
      :clickable="mode === 'edit'"
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @add-new="onAddTraderSite"
      @table-item-clicked="onSelectTraderSite"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>

    <CustomerDetailsModalTraderSites
      v-if="isAddTraderSiteModalOpen && modifiedTraderSite"
      :isNew="isNewTraderSite"
      :trader-site="modifiedTraderSite"
      :mode="traderSiteModalMode"
      :trader-name="traderName"
      @close="isAddTraderSiteModalOpen = false"
      @save="onSaveTraderSite" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { deepCopy } from '@/utils/data';
import { filterData, sortData } from '@/utils/table';
import BaseTable from '../common/BaseTable.vue';
import CustomerDetailsModalTraderSites from './CustomerDetailsModalTraderSites.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';

import type { TraderSite } from '@/types/Traders';
import { RecordMode, type TableFilterData } from '@/types/Base';

const props = defineProps<{
  traderSites: TraderSite[];
  mode: 'view' | 'edit';
  traderName?: string;
}>();

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
}>({
  query: '',
  column: undefined,
  order: undefined,
});

const filteredSites = computed<TraderSite[]>(() => {
  let list = props.traderSites.filter(
    (x) => x.recordMode !== RecordMode.Deleted && !x.isDefault && !x.isHQ
  );

  if (filterState.query) {
    list = filterData(list, filterState.query, ['name', 'code', 'city', 'address', 'zipCode']);
  }

  if (filterState.column !== undefined && filterState.order) {
    list = sortData(list, filterState.column, filterState.order, tableHeaders);
  }

  return list;
});

const emit = defineEmits(['saveTraderSite', 'refreshData']);

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  {
    value: 'name',
    text: $t('customer.traderSiteName'),
    type: 'text',
    active: true,
  },
  {
    value: 'code',
    text: $t('customer.traderSiteCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'city',
    text: $t('customer.traderSiteCity'),
    type: 'text',
    active: true,
  },
  {
    value: 'address',
    text: $t('customer.traderSiteAddress'),
    type: 'text',
    active: true,
  },
  {
    value: 'zipCode',
    text: $t('customer.traderSiteZipCode'),
    type: 'text',
    active: true,
  },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
  { value: 'isHQ', text: $t('customer.traderSiteIsHQ'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const isAddTraderSiteModalOpen = ref(false);
const modifiedTraderSite = ref<TraderSite>();
const isNewTraderSite = ref(false);
const traderSiteModalMode = ref<'view' | 'edit'>('view');
function initTraderSite(): TraderSite {
  return {
    id: uuidv4(),
    traderId: '',
    isBranch: false,
    isHQ: false,
    isDefault: false,
    isActive: true,
    isFromManager: true,
  };
}
function onAddTraderSite() {
  modifiedTraderSite.value = initTraderSite();
  modifiedTraderSite.value.recordMode = RecordMode.Added;
  traderSiteModalMode.value = 'edit';
  isNewTraderSite.value = true;
  isAddTraderSiteModalOpen.value = true;
}

function onSelectTraderSite(site: TraderSite) {
  modifiedTraderSite.value = site;
  modifiedTraderSite.value.recordMode = RecordMode.Modified;
  traderSiteModalMode.value = site.isFromManager ? 'edit' : 'view';
  isNewTraderSite.value = false;
  isAddTraderSiteModalOpen.value = true;
}

function onSaveTraderSite(site: TraderSite) {
  isAddTraderSiteModalOpen.value = false;
  emit('saveTraderSite', deepCopy(site));
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<TraderSite>();
function onDeleteRow(item: TraderSite) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  deleteRowItem.value.recordMode = RecordMode.Deleted;
  modalDeleteRow.value = false;
  onSaveTraderSite(deleteRowItem.value);
}

function onDeleteMultiselect(items: TraderSite[]) {
  items.forEach((item) => {
    if (item.isFromManager) {
      item.recordMode = RecordMode.Deleted;
    }
    onSaveTraderSite(item);
  });
}

function onRefreshData(options: TableFilterData) {
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }
}
</script>
